import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { CircleNotch, Video, FirstAid } from '@phosphor-icons/react';
import PatientSuggest from '../PatientSuggest';
import ICD10Search from '../ICD10Search';
import { formatCurrency } from '../../lib/utils';

export const BHClaimModal = ({
  open, onOpenChange, formData, setFormData, onChange,
  onSubmit, saving, patients, practitioners,
  bhCodes, bhConfig, selectedCategory, setSelectedCategory, filteredCodes,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle style={{ fontFamily: 'Manrope' }}>New Behavioral Health Claim</DialogTitle>
        <DialogDescription>Create a claim for OhioMHAS behavioral health services</DialogDescription>
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Patient *</Label>
            <PatientSuggest
              onSelect={(p) => onChange('patient_id', p.id)}
              placeholder="Type patient name or Medicaid ID..."
              value={formData.patient_id ? `${patients.find((p) => p.id === formData.patient_id)?.first_name} ${patients.find((p) => p.id === formData.patient_id)?.last_name}` : ''}
            />
          </div>
          <div className="space-y-2">
            <Label>Practitioner *</Label>
            <Select value={formData.practitioner_id || 'select'} onValueChange={(v) => onChange('practitioner_id', v === 'select' ? '' : v)}>
              <SelectTrigger data-testid="bh-practitioner"><SelectValue placeholder="Select practitioner" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="select" disabled>Select practitioner</SelectItem>
                {practitioners.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.job_title || 'Staff'})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Service Category</Label>
              <Select value={selectedCategory || 'all'} onValueChange={(v) => { setSelectedCategory(v === 'all' ? '' : v); onChange('service_code', ''); }}>
                <SelectTrigger><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(bhConfig.categories || {}).map(([code, name]) => (
                    <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Service Date *</Label>
              <Input type="date" value={formData.service_date} onChange={(e) => onChange('service_date', e.target.value)} required data-testid="bh-service-date" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Service Code *</Label>
            <Select value={formData.service_code || 'select'} onValueChange={(v) => onChange('service_code', v === 'select' ? '' : v)}>
              <SelectTrigger data-testid="bh-service-code"><SelectValue placeholder="Select service code" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="select" disabled>Select service code</SelectItem>
                {filteredCodes.map(([code, info]) => (
                  <SelectItem key={code} value={code}>
                    <span className="font-mono">{code}</span> - {info.description} (${info.rate})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.service_code && bhCodes[formData.service_code] && (
              <div className="p-3 bg-indigo-50 rounded-lg text-sm">
                <div className="font-medium text-indigo-900">{bhCodes[formData.service_code].description}</div>
                <div className="text-indigo-700">Rate: ${bhCodes[formData.service_code].rate} per {bhCodes[formData.service_code].unit_type}</div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Units *</Label>
              <Input type="number" min="0.25" step="0.25" value={formData.units} onChange={(e) => onChange('units', e.target.value)} required data-testid="bh-units" />
            </div>
            <div className="space-y-2">
              <Label>Estimated Amount</Label>
              <div className="h-10 px-3 py-2 bg-slate-100 rounded-md font-mono text-lg">
                {formData.service_code && bhCodes[formData.service_code]
                  ? formatCurrency(bhCodes[formData.service_code].rate * parseFloat(formData.units || 0))
                  : '$0.00'}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4 border-t pt-4">
          <h3 className="font-semibold flex items-center gap-2"><FirstAid size={18} />Diagnosis (ICD-10)</h3>
          <ICD10Search
            value={formData.diagnosis_code}
            description={formData.diagnosis_description}
            onChange={(code, desc) => setFormData((prev) => ({ ...prev, diagnosis_code: code, diagnosis_description: desc }))}
            label="Primary Diagnosis *"
            required
            testId="bh-diagnosis-search"
          />
        </div>

        <div className="space-y-4 border-t pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Place of Service *</Label>
              <Select value={formData.place_of_service} onValueChange={(v) => onChange('place_of_service', v)}>
                <SelectTrigger data-testid="bh-pos"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(bhConfig.place_of_service_codes || {}).map(([code, name]) => (
                    <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Prior Authorization #</Label>
              <Input value={formData.prior_auth_number} onChange={(e) => onChange('prior_auth_number', e.target.value)} placeholder="If required" data-testid="bh-prior-auth" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Checkbox id="telehealth" checked={formData.is_telehealth} onCheckedChange={(checked) => onChange('is_telehealth', checked)} data-testid="bh-telehealth" />
            <Label htmlFor="telehealth" className="flex items-center gap-2 cursor-pointer">
              <Video size={16} />Telehealth Service (adds GT modifier)
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            type="submit"
            className="bg-indigo-900 hover:bg-indigo-800"
            disabled={saving || !formData.patient_id || !formData.practitioner_id || !formData.service_code || !formData.diagnosis_code}
            data-testid="save-bh-claim-btn"
          >
            {saving ? <CircleNotch size={18} className="animate-spin" /> : 'Create Claim'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);
